// @flow
import React, { Children, cloneElement, useContext, type Node } from 'react';
import classnames from 'classnames';
import { isEmpty, isFunction } from 'lodash';

import { ARROW_DOWN, ARROW_LEFT, ARROW_RIGHT, ARROW_UP, END, HOME } from 'Utils/keyCodes';
import { getTabsBackgroundClasses } from 'Components/utils/tabs/tabs.util';
import { withOpenAndScrollToHash } from 'Components/utils/tabs/tabs.hoc.jsx';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';

import { ActiveIndexes } from 'Components/frameworks/active-indexes/ActiveIndexes.jsx';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer.jsx';
import { Scrollable } from 'Components/frameworks/scrollable/Scrollable.jsx';
import { TabHeader } from 'Components/frameworks/tab-header/TabHeader.jsx';
import { type IconName } from 'Components/frameworks/icon/Icon.jsx';

import { themes } from './Tabs.themes';

export type TabsItem = {|
  customHeader?: ?(*) => Node,
  header?: {
    color?: Colors,
    iconName?: IconName,
    title: Node,
  },
  id?: string,
  headerId: string,
  panelId: string,
  content: Node,
|};

type TabsProps = {
  areAllCollapsable?: boolean,
  bgColor?: Colors,
  className?: string,
  collapseIconName?: IconName,
  containerSize?: 'mini' | 'small' | 'medium' | 'large',
  expandIconName?: IconName,
  hasBackground?: boolean,
  isContainerWide?: boolean,
  isContentUnmountedOnCollapse?: boolean,
  id?: string,
  initialSelectedItem?: number,
  isScrollable?: boolean,
  items: Array<TabsItem>,
  onSelect?: (index: number) => void,
  onTabClick?: (labelGA: string, index: number) => void,
  scrollBackwardLabel?: string,
  scrollForwardLabel?: string,
  slotLeft?: Node,
  slotRight?: Node,
  theme?: string,
};

function _Tabs(props: TabsProps) {
  const {
    areAllCollapsable,
    bgColor,
    className,
    collapseIconName,
    containerSize,
    expandIconName,
    hasBackground,
    isContainerWide,
    isContentUnmountedOnCollapse,
    initialSelectedItem = 0,
    id,
    isScrollable,
    items = [],
    onSelect,
    onTabClick,
    scrollForwardLabel,
    scrollBackwardLabel,
    slotLeft,
    slotRight,
    theme = 'default',
  } = props;

  const { isRTL } = useContext(ResponsiveContext);

  if (isEmpty(items)) return null;

  const refsArray = [];

  const { thBody, thHeader, thList, thListItem, thSlot } = themes[theme](props);

  const tabUpdate = (idx, update) => {
    update(idx);
    refsArray[idx] && refsArray[idx].focus();
  };

  const onKeyDown = ({ e, index, update }) => {
    if (items.length > 1) {
      const lastItem = items.length - 1;

      switch (e.which) {
        case ARROW_RIGHT:
        case ARROW_DOWN:
          e.preventDefault();
          tabUpdate(index === lastItem ? 0 : index + 1, update);
          break;
        case ARROW_LEFT:
        case ARROW_UP:
          e.preventDefault();
          tabUpdate(index === 0 ? lastItem : index - 1, update);
          break;
        case HOME:
          e.preventDefault();
          tabUpdate(0, update);
          break;
        case END:
          e.preventDefault();
          tabUpdate(lastItem, update);
          break;
        default:
      }
    }
  };

  const tabsCount = items.filter(Boolean).length;

  return (
    <ActiveIndexes
      activeIndexes={[initialSelectedItem]}
      hasNoIndexEnabled={areAllCollapsable}
      onUpdate={onSelect}
    >
      {({ update, activeIndexes, lastIndex }) => {
        const els = items
          .filter(Boolean)
          .map(({ customHeader, header = {}, headerId, panelId }, index) => {
            const onClick = () => {
              tabUpdate(index, update);
              onTabClick && onTabClick(headerId, index);
            };

            const isActive = activeIndexes.includes(index);

            const setRef = ref => {
              refsArray[index] = ref;
            };

            const { title, ...rest } = header;

            const headerProps = {
              ...rest,
              controlsId: panelId,
              id: headerId,
              isActive,
              isDisabled: tabsCount <= 1,
              onClick: onClick,
              onKeyDown: e => onKeyDown({ e, index, update }),
              style: isRTL && isScrollable ? { direction: 'rtl' } : null,
              tabIndex: isActive || index === lastIndex ? 0 : -1,
            };

            if (isFunction(customHeader)) {
              return customHeader(headerProps, setRef);
            }

            return (
              <TabHeader
                {...headerProps}
                className={thListItem}
                collapseIconName={collapseIconName}
                expandIconName={expandIconName}
                key={headerId}
                ref={setRef}
                role="tab"
                style={isRTL && isScrollable ? { direction: 'rtl' } : null}
                theme={theme}
              >
                {title}
              </TabHeader>
            );
          });

        const tabsList = (
          <div className={classnames('reset-list flex w-full', thList)} role="tablist">
            {els}
          </div>
        );

        return (
          <section
            className={className}
            id={id}
            data-cs-override-id="Tabs" // ContentSquare must ignore this id see https://docs.contentsquare.com/uxa-fr/#ignorer-remplacer-ou-ajouter-un-id
            data-testid="tabs"
          >
            <SiteContainer isWide={isContainerWide} size={containerSize}>
              <div className={classnames('flex', thHeader)}>
                {slotLeft && <div className={thSlot}>{slotLeft}</div>}
                {isScrollable ? (
                  <Scrollable
                    backwardLabel={scrollBackwardLabel}
                    forwardLabel={scrollForwardLabel}
                    hasShadow
                    navPosition="side"
                  >
                    {tabsList}
                  </Scrollable>
                ) : (
                  tabsList
                )}
                {slotRight && <div className={thSlot}>{slotRight}</div>}
              </div>
            </SiteContainer>
            <div
              data-testid="tabs-items-container"
              className={classnames(
                {
                  // $FlowFixMe
                  [getTabsBackgroundClasses(hasBackground, bgColor)]:
                    activeIndexes.length && !activeIndexes.includes(-1),
                },
                thBody,
              )}
            >
              {items.filter(Boolean).map(({ content, headerId, panelId }, index) => {
                const isActive = activeIndexes.includes(index);

                return (
                  (!isContentUnmountedOnCollapse || isActive) && (
                    <div
                      aria-hidden={!isActive}
                      aria-labelledby={headerId}
                      className={classnames({
                        'h-0 invisible overflow-hidden pointer-events-none': !isActive,
                        fadeIn: isActive,
                      })}
                      data-cs-override-id="Tabs-panel" // ContentSquare must ignore this id see https://docs.contentsquare.com/uxa-fr/#ignorer-remplacer-ou-ajouter-un-id
                      id={panelId}
                      key={panelId}
                      role="tabpanel"
                      data-testid={`tabs-item-${index}-content`}
                    >
                      <SiteContainer isWide={isContainerWide} size={containerSize}>
                        {Children.map(
                          content,
                          (el, i) => (el ? cloneElement(el, { key: `${panelId}-${i}` }) : null),
                        )}
                      </SiteContainer>
                    </div>
                  )
                );
              })}
            </div>
          </section>
        );
      }}
    </ActiveIndexes>
  );
}

export const Tabs = withOpenAndScrollToHash(_Tabs);
